import { useState, useEffect } from 'react';
import { useNavigate, generatePath, NavLink } from 'react-router-dom';
import {
  Select,
  IconLoader,
  ButtonPrimary,
  ButtonPseudo,
  ButtonSecondary,
  IconEdit,
  usePrevious,
  SelectItemProps,
  IconTrash,
  Popup,
  IconCopyTwo as IconCopy,
  ResponseError,
  IconChangeLoClock,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  TariffItem,
  RiskScoreEnum,
  TariffType,
  TariffStatus,
} from '../../hoc/tariffs/types';
import PublishSidepanel from './publish-sidepanel';
import ArchiveSidepanel from './archive-sidepanel';
import { clearTariffPageState } from '../../hoc/tariffs/store/slices/tariff-page';
import {
  useDuplicateTariffMutation,
  useDeleteTariffMutation,
  useToggleTariffDefaultMutation,
} from '../../hoc/tariffs/store/services';
import { GeneralInfoTypes } from './types';

import { TariffsRoutes } from '../../routes';

import cn from './tariffs.module.css';

const getStatus = (status: TariffStatus) => {
  let statusName;
  let statusClassName;

  switch (status) {
    case TariffStatus.DRAFT:
      statusName = 'Draft';
      statusClassName = cn.tariffDraftStatus;
      break;
    case TariffStatus.ARCHIVED:
      statusName = 'Archived';
      statusClassName = cn.tariffArchivedStatus;
      break;
    case TariffStatus.PUBLISHED:
      statusName = 'Published';
      statusClassName = cn.tariffPublishedStatus;
      break;
    default:
      statusName = status;
      break;
  }

  return { statusName, statusClassName };
};

const GeneralInfo = ({
  isLoading,
  generalInfo,
  onStatusChange,
  onRiskScroreChange,
  onTariffNameChange,
  onTariffTypeChange,
  statusChangeInProgress,
  statusChangeSuccess,
  changeNameInProgress,
  riskScoreRanges,
  isEditable,
  tariffId,
  auditLogAvailable,
}: GeneralInfoTypes) => {
  const [editEnabled, setEditEnabled] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [publishSidePanelVisible, setPublishSidepaneVisible] = useState(false);
  const [archiveSidePanelVisible, setArchiveSidepaneVisible] = useState(false);
  const [defaultPopupVisible, setDefaultPopupVisible] = useState(false);
  const [tariffName, setTariffName] = useState(generalInfo?.name);
  const [duplicateError, setDupliateError] = useState('');

  const prevChangeNameInProgress = usePrevious(changeNameInProgress);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [duplicate, { isLoading: requestInProgress, isError }] =
    useDuplicateTariffMutation();
  const [deleteTariff, { isLoading: deleteInProgress }] =
    useDeleteTariffMutation();
  const [
    makeDefault,
    { isLoading: defaultInProgress, isSuccess: defaultIsSuccess },
  ] = useToggleTariffDefaultMutation();

  const onMakeDefault = () => {
    makeDefault({
      id: generalInfo?.id as string,
      body: {
        default: true,
      },
    });
  };

  const dupliateTariff = async () => {
    const response = (await duplicate(generalInfo?.id as string)) as {
      data: { data: TariffItem };
    };

    if (response.data) {
      setPopupVisible(false);
      dispatch(clearTariffPageState());

      navigate(
        generatePath(TariffsRoutes.TARIFFS_INFO, {
          tariffId: response.data.data.id,
        }),
      );
    }
  };

  const onDeleteTariff = async () => {
    const response = (await deleteTariff(generalInfo?.id as string)) as {
      error: ResponseError;
    };

    if (!response.error) {
      setDeletePopupVisible(false);
      dispatch(clearTariffPageState());

      navigate(TariffsRoutes.TARIFFS_LIST);
    }
  };

  useEffect(() => {
    if (isError) {
      setDupliateError('Internal Server Error');
    }
  }, [isError]);

  useEffect(() => {
    if (generalInfo?.name) {
      setTariffName(generalInfo?.name);
    }
  }, [generalInfo?.name]);

  useEffect(() => {
    if (!changeNameInProgress && prevChangeNameInProgress) {
      setEditEnabled(false);
    }
  }, [prevChangeNameInProgress, changeNameInProgress]);

  useEffect(() => {
    if (defaultIsSuccess) {
      setDefaultPopupVisible(false);
    }
  }, [defaultIsSuccess]);

  let content;
  let status: SelectItemProps = {
    name: 'Publish',
    value: TariffStatus.PUBLISHED,
  };

  if (generalInfo?.status === TariffStatus.PUBLISHED) {
    status = {
      name: 'Archive',
      value: TariffStatus.ARCHIVED,
    };
  }

  const statusProps = getStatus(generalInfo?.status as TariffStatus);

  if (isLoading) {
    content = (
      <div className={cn.generalInfoLoader}>
        <IconLoader />
      </div>
    );
  } else {
    let nameContent;
    let defaultContent;

    if (editEnabled) {
      nameContent = (
        <input
          type="text"
          name="name"
          value={tariffName}
          onChange={(e) => setTariffName(e.target.value)}
          className={`${cn.tariffNameInput} ${
            editEnabled ? cn.tariffNameInputActive : ''
          }`}
          disabled={!editEnabled}
        />
      );
    } else {
      nameContent = <div className={cn.tariffName}>{generalInfo?.name}</div>;
    }

    if (generalInfo?.isDefault) {
      defaultContent = (
        <div className={cn.tariffDefault}>
          <div className={cn.hintText}>
            Default tariff is automatically assigned for a combination of client
            type and risk scoring category.
          </div>
          Default <span>i</span>
        </div>
      );
    } else if (
      !generalInfo?.isDefault &&
      generalInfo?.status === TariffStatus.PUBLISHED
    ) {
      defaultContent = (
        <button
          type="button"
          className={cn.makeDefault}
          onClick={() => setDefaultPopupVisible(true)}
        >
          Make as Default
        </button>
      );
    }

    content = (
      <div className={cn.generalInfo}>
        <div className={cn.generalInfoTop}>
          <div className={cn.tariffNameWrapper}>
            <div className={cn.tariffNameLeft}>
              {nameContent}
              {!editEnabled && isEditable && (
                <button
                  type="button"
                  aria-label="Edit button"
                  className={cn.editButton}
                  onClick={() => setEditEnabled(true)}
                >
                  <IconEdit width={22} height={22} />
                </button>
              )}
              {editEnabled && (
                <>
                  <ButtonPrimary
                    text={changeNameInProgress ? 'Loading...' : 'Save'}
                    onClick={() => onTariffNameChange(tariffName as string)}
                    bold
                  />
                  <ButtonPseudo
                    text="Cancel"
                    onClick={() => setEditEnabled(false)}
                    className={`button-white ${cn.cancelButton}`}
                    bold
                  />
                </>
              )}
            </div>
            <div className={cn.tariffControls}>
              {isEditable && (
                <ButtonSecondary
                  text="Copy tariff"
                  icon={<IconCopy />}
                  className={cn.copyButton}
                  onClick={() => setPopupVisible(true)}
                  bold
                />
              )}
              {generalInfo?.status === TariffStatus.DRAFT && isEditable && (
                <ButtonSecondary
                  text="Delete draft"
                  icon={<IconTrash />}
                  className={cn.deleteButton}
                  onClick={() => setDeletePopupVisible(true)}
                  bold
                />
              )}
              {isEditable && (
                <ButtonPrimary
                  text={status.name}
                  onClick={() => {
                    if (generalInfo?.status === TariffStatus.PUBLISHED) {
                      setArchiveSidepaneVisible(true);
                    } else {
                      setPublishSidepaneVisible(true);
                    }
                  }}
                  bold
                />
              )}
            </div>
          </div>
          <div className={cn.tariffPageInfo}>
            {defaultContent}
            <div
              className={`${cn.tariffStatus} ${statusProps.statusClassName}`}
            >
              {statusProps.statusName}
            </div>
          </div>
        </div>
        <div className={cn.generalInfoInformation}>
          {generalInfo?.status !== TariffStatus.DRAFT && (
            <div className={cn.generalInfoStatistic}>
              {/* <div className={cn.generalInfoStatisticItem}>
              <div className={cn.generalInfoStatisticItemTitle}>
                Received in 24 H
              </div>
              <div className={cn.generalInfoStatisticItemValue}>
                <div />
              </div>
            </div>
            <div className={cn.generalInfoStatisticItem}>
              <div className={cn.generalInfoStatisticItemTitle}>
                Received in 7 D
              </div>
              <div className={cn.generalInfoStatisticItemValue}>
                <div />
              </div>
            </div>
            <div className={cn.generalInfoStatisticItem}>
              <div className={cn.generalInfoStatisticItemTitle}>
                Applied for clients
              </div>
              <div className={cn.generalInfoStatisticItemValue}>0</div>
            </div>
            <div className={cn.generalInfoStatisticItem}>
              <div className={cn.generalInfoStatisticItemTitle}>
                Average profitability per client
              </div>
              <div className={cn.generalInfoStatisticItemValue}>
                <div />
              </div>
            </div> */}
            </div>
          )}
          {auditLogAvailable && (
            <NavLink
              to={`/administration/change-logs?entityType=TARIFF&entityId=${tariffId}`}
              className={cn.changeLogLink}
              target="_blank"
            >
              <IconChangeLoClock />
              Change Log
            </NavLink>
          )}
        </div>

        <div className={cn.generalInfoControls}>
          <div className={cn.generalInfoControl}>
            <Select
              list={[
                {
                  name: 'Individual',
                  value: TariffType.INDIVIDUAL,
                },
                {
                  name: 'Corporate',
                  value: TariffType.CORPORATE,
                },
              ]}
              placeholder=""
              name="clientType"
              value={generalInfo?.type}
              onChange={(e) => onTariffTypeChange(e.target.value as TariffType)}
              disabled={generalInfo?.status !== TariffStatus.DRAFT}
              title="Client Type"
            />
          </div>
          <div className={cn.generalInfoControl}>
            <Select
              list={riskScoreRanges}
              placeholder=""
              name="riskScore"
              value={generalInfo?.riskScoreRangeId}
              onChange={(e) =>
                onRiskScroreChange(e.target.value as RiskScoreEnum)
              }
              title="Risk score"
              disabled={!isEditable}
            />
          </div>
        </div>
        <Popup
          title="Create a copy of the tariff?"
          onCancelClick={() => setPopupVisible(false)}
          onOkClick={dupliateTariff}
          cancelButtonText="Cancel"
          okButtonText={requestInProgress ? 'Loading...' : 'Copy'}
          isVisible={popupVisible}
        >
          <div>
            {duplicateError && (
              <div className={cn.duplicateError}>{duplicateError}</div>
            )}
          </div>
        </Popup>
        <Popup
          title="Delete draft tariff?"
          onCancelClick={() => setDeletePopupVisible(false)}
          onOkClick={onDeleteTariff}
          cancelButtonText="Cancel"
          okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
          isVisible={deletePopupVisible}
          warning
        />
        <Popup
          title="Make tariff default?"
          onCancelClick={() => setDefaultPopupVisible(false)}
          onOkClick={onMakeDefault}
          cancelButtonText="Cancel"
          okButtonText={defaultInProgress ? 'Loading...' : 'Make as default'}
          isVisible={defaultPopupVisible}
        />
        <PublishSidepanel
          panelIsOpen={publishSidePanelVisible}
          setPanelIsOpen={setPublishSidepaneVisible}
          status={status.value as TariffStatus}
          onStatusChange={onStatusChange}
          statusChangeSuccess={statusChangeSuccess}
          statusChangeInProgress={statusChangeInProgress as boolean}
          tariffId={tariffId}
        />
        <ArchiveSidepanel
          panelIsOpen={archiveSidePanelVisible}
          setPanelIsOpen={setArchiveSidepaneVisible}
          status={status.value as TariffStatus}
          onStatusChange={onStatusChange}
          statusChangeSuccess={statusChangeSuccess}
          statusChangeInProgress={statusChangeInProgress as boolean}
          tariffId={tariffId}
        />
      </div>
    );
  }

  return content;
};

export default GeneralInfo;
